import { useEffect, useState } from 'react'
import { CheckIcon, CopyIcon, DownloadIcon, ExternalLinkIcon, QrCodeIcon } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  detectPlatform,
  displayUrl,
  isDirectInstall,
  mobileApps,
  type MobileApp,
  type Platform,
} from '@/components/layout/mobileApps'

/** How long "Copied" stays on the button before it reads "Copy link" again. */
const COPIED_FEEDBACK_MS = 2000

/**
 * The companion iOS apps, at the foot of the sidebar above the account menu. On a desktop each
 * opens a card with a QR code to scan with the phone; on an iPhone or iPad the entry follows the
 * install link instead. Nothing is rendered while no app has an install link configured, nor on
 * any other phone or tablet, which can neither scan its own screen nor install an iOS app.
 */
export function NavApps({
  apps = mobileApps,
  platform = detectPlatform(),
}: {
  apps?: MobileApp[]
  platform?: Platform
}) {
  const { t } = useTranslation()

  if (apps.length === 0 || platform === 'otherMobile') {
    return null
  }

  return (
    // `mt-auto` pushes the group down onto the footer, however short the navigation above is.
    <SidebarGroup className="mt-auto" role="region" aria-label={t('apps.group')}>
      <SidebarGroupLabel>{t('apps.group')}</SidebarGroupLabel>
      <SidebarMenu>
        {apps.map((app) => (
          <SidebarMenuItem key={app.name}>
            {platform === 'ios' ? <AppInstallLink app={app} /> : <AppInstallCard app={app} />}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

/** The app's icon at sidebar size, rounded the way the home screen rounds it. */
function AppIcon({ app, className }: { app: MobileApp; className?: string }) {
  return <img src={app.icon} alt="" aria-hidden className={className ?? 'size-4 shrink-0 rounded-[4px]'} />
}

/** On the phone itself: the entry is the install link. */
function AppInstallLink({ app }: { app: MobileApp }) {
  const direct = isDirectInstall(app.url)
  const TrailingIcon = direct ? DownloadIcon : ExternalLinkIcon

  return (
    <SidebarMenuButton asChild tooltip={app.name}>
      {/* A direct install opens no page, so a new tab would only be left behind empty. */}
      <a
        href={app.url}
        {...(!direct && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        <AppIcon app={app} />
        <span className="truncate">{app.name}</span>
        <TrailingIcon className="text-muted-foreground ml-auto" />
      </a>
    </SidebarMenuButton>
  )
}

/** On a desktop: the entry opens a card with the QR code to scan with the phone. */
function AppInstallCard({ app }: { app: MobileApp }) {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const direct = isDirectInstall(app.url)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) {
      return
    }
    const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
    return () => window.clearTimeout(timer)
  }, [copied])

  async function copy() {
    try {
      await navigator.clipboard.writeText(app.url)
      setCopied(true)
    } catch {
      // Denied permission, or an insecure origin. Worth a toast: unlike success, it needs reading.
      toast.error(t('apps.copyFailed'))
    }
  }

  const steps = direct
    ? [t('apps.steps.scan'), t('apps.steps.install'), t('apps.steps.trust')]
    : [t('apps.steps.scan'), t('apps.steps.get')]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          tooltip={app.name}
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group/app"
        >
          <AppIcon app={app} />
          <span className="truncate">{app.name}</span>
          <QrCodeIcon className="text-muted-foreground group-hover/app:text-sidebar-accent-foreground ml-auto" />
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        side={isMobile ? 'top' : 'right'}
        align="end"
        sideOffset={12}
        className="w-80 gap-0 overflow-hidden p-0"
      >
        <div className="flex items-center gap-3 p-4">
          <AppIcon app={app} className="size-11 shrink-0 rounded-[10px] shadow-sm" />
          <PopoverHeader className="min-w-0">
            <PopoverTitle className="truncate text-base">{app.name}</PopoverTitle>
            <PopoverDescription className="text-xs">
              {t(direct ? 'apps.distribution.direct' : 'apps.distribution.appStore')}
            </PopoverDescription>
          </PopoverHeader>
        </div>

        <div className="bg-muted/50 border-y px-4 py-5">
          {/* Always dark on white, in the dark theme too: a camera reads an inverted code badly.
              The app icon in the middle costs some of the code's redundancy, hence level H. */}
          <div className="mx-auto w-fit rounded-xl bg-white p-3 shadow-sm ring-1 ring-black/5">
            <QRCodeSVG
              value={app.url}
              size={168}
              level="H"
              marginSize={0}
              imageSettings={{ src: app.icon, height: 36, width: 36, excavate: true }}
              title={t('apps.qrCode', { name: app.name })}
              role="img"
            />
          </div>
        </div>

        <ol className="space-y-2.5 p-4">
          {steps.map((step, index) => (
            <li key={step} className="flex gap-3 text-xs leading-5">
              <span
                aria-hidden
                className="bg-primary/10 text-primary flex size-5 shrink-0 items-center justify-center rounded-full text-[0.7rem] font-semibold tabular-nums"
              >
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <div className="flex items-center gap-2 border-t px-4 py-3">
          {/* A desktop browser cannot follow an itms-services link, so it is shown, not linked. */}
          {direct ? (
            <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
              {displayUrl(app.url)}
            </span>
          ) : (
            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground min-w-0 flex-1 truncate text-xs underline-offset-4 hover:underline"
            >
              {displayUrl(app.url)}
            </a>
          )}
          <Button size="sm" variant="outline" onClick={() => void copy()} className="min-w-28">
            {copied ? <CheckIcon className="text-primary" /> : <CopyIcon />}
            {copied ? t('apps.copied') : t('apps.copy')}
          </Button>
          {/* Outside the button: a live region inside one whose name changes along with it is
              announced unreliably. Empty until there is something to say. */}
          <span role="status" className="sr-only">
            {copied ? t('apps.copied') : ''}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  )
}
